/**
 * The ONE definition of how Total Check-ins is broken down on Event Performance.
 *
 * Why this exists as a shared list rather than inline JSX: the page previously added
 * `unclassified_member_checkins` and `unclassified_nonmember_checkins` into the displayed Total
 * while rendering neither column, so the visible numbers did not add up to the total sitting
 * beside them and nobody could see why. The CSV included those columns, so the export and the
 * screen disagreed too.
 *
 * Header cells, body cells, the total, and the CSV all derive from this array. A new channel is
 * added in exactly one place, and it is not possible to include a component in the total while
 * leaving it off the screen.
 */
import type { EventExecutiveSummaryRow } from "../services/eventExecutiveSummary.service";

export interface CheckinComponent {
  /** Column on EventExecutiveSummaryRow. */
  key: keyof Pick<
    EventExecutiveSummaryRow,
    | "member_self_checkins"
    | "nonmember_self_checkins"
    | "member_operator_checkins"
    | "nonmember_operator_checkins"
    | "cta_member_checkins"
    | "cta_nonmember_checkins"
    | "unclassified_member_checkins"
    | "unclassified_nonmember_checkins"
  >;
  /** Table header. */
  label: string;
  /** CSV header — spelled out, because a CSV has no tooltip to explain it. */
  csvLabel: string;
}

export const CHECKIN_COMPONENTS: ReadonlyArray<CheckinComponent> = [
  {
    key: "member_self_checkins",
    label: "Self · Member",
    csvLabel: "Member Self Check-ins",
  },
  {
    key: "nonmember_self_checkins",
    label: "Self · Nonmember",
    csvLabel: "Nonmember Self Check-ins",
  },
  {
    key: "member_operator_checkins",
    label: "Operator · Member",
    csvLabel: "Operator-assisted Member Check-ins",
  },
  {
    key: "nonmember_operator_checkins",
    label: "Operator · Nonmember",
    csvLabel: "Operator-assisted Nonmember Check-ins",
  },
  // Auto-check-ins from an event CTA membership signup. Nobody walked up to a door, so these are
  // neither self-service nor operator-assisted; before they had no category and were reported as
  // unclassified.
  {
    key: "cta_member_checkins",
    label: "CTA · Member",
    csvLabel: "CTA Conversion Member Check-ins",
  },
  {
    key: "cta_nonmember_checkins",
    label: "CTA · Nonmember",
    csvLabel: "CTA Conversion Nonmember Check-ins",
  },
  // Genuinely unknown provenance: the check-in happened before the channel was recorded durably.
  // Shown, never guessed into one of the categories above.
  {
    key: "unclassified_member_checkins",
    label: "Unclassified · Member",
    csvLabel: "Unclassified Member Check-ins",
  },
  {
    key: "unclassified_nonmember_checkins",
    label: "Unclassified · Nonmember",
    csvLabel: "Unclassified Nonmember Check-ins",
  },
];

/** Total Check-ins = the sum of exactly the components rendered on screen. */
export const totalCheckins = (row: EventExecutiveSummaryRow): number =>
  CHECKIN_COMPONENTS.reduce((sum, c) => sum + (row[c.key] ?? 0), 0);
